import { useEffect, useRef, useState } from 'react';

import { Button } from '@coingate/ui/dist/components/Button';
import { Request } from '@javascript/helpers';
import { getErrorMessage } from '@shared/errors';
import TurnstileWidget, { type TurnstileWidgetHandle } from '@shared/components/TurnstileWidget';
import { useAnalytics } from '@shared/hooks/useAnalytics';

export const VerifyStep = ({
  email,
}: {
  email: string;
}) => {
  const { trackEvent } = useAnalytics('authentication');

  const turnstileRef = useRef<TurnstileWidgetHandle>(null);
  const [isTurnstileReady, setIsTurnstileReady] = useState(true);
  const [countdown, setCountdown] = useState(59);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (countdown <= 0) return;

    const timer = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [countdown]);

  const handleResend = async () => {
    if (isResending) return;
    setIsResending(true);
    setError(null);

    trackEvent('signup_verify_resend_click');

    try {
      const turnstileToken = (await turnstileRef.current?.getResponsePromise()) ?? null;

      await Request('/users/confirmation', 'POST', {
        body: { user: { email }, 'cf-turnstile-response': turnstileToken },
      });

      trackEvent('signup_verify_resend_success');
    } catch (err) {
      trackEvent('signup_verify_resend_failed', { error: err });

      setError(getErrorMessage(err));
    } finally {
      // A Turnstile token is single-use, so every attempt needs a fresh one.
      turnstileRef.current?.reset();
      setCountdown(59);
      setIsResending(false);
    }
  };

  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-6">
        <div className="font-normal text-base text-gray-600">
          Confirmation sent to <span className="font-semibold text-gray-900 break-all block">{email}.</span>
        </div>

        <div className="font-normal text-base text-gray-600">
          Click the link in your email on this device.
        </div>

        <TurnstileWidget ref={turnstileRef} onTokenChange={(token) => setIsTurnstileReady(token !== null)} />

        {error && (
          <div className="font-normal text-sm text-red-600" role="alert">
            {error}
          </div>
        )}

        <Button
          data-testid="continue-with-email"
          className="w-full text-base font-semibold"
          size="lg"
          disabled={countdown > 0 || isResending || !isTurnstileReady}
          variant="primary"
          onClick={handleResend}
        >
          {countdown > 0 ? `Resend email (${countdown}s)` : 'Resend email'}
        </Button>
      </div>
    </div>
  )
}

export default VerifyStep
